import { z } from "zod";
import { getServicesHandler, isIOSApp } from "./ios";
import { isAndroidApp } from "./android";

/**
 * Runs a partner OAuth round-trip in a sheet the native shell owns, instead of
 * navigating the app's own WebView to the partner domain.
 *
 * The full-page redirect had three faults on a phone: the partner page took
 * over the only WebView the app has, it painted under the notch and the home
 * indicator because a foreign page knows nothing of our safe-area handling,
 * and a partner outage left the reader on a dead page with no chrome to escape
 * it — the app had to be killed. A sheet the shell presents keeps the app
 * alive underneath, insets its content, and always carries a close control.
 *
 * The sheet loads `url`, watches every navigation, and ends the session as
 * soon as one reaches `redirectUri` (compared on origin and path, which is
 * what the OAuth server has registered). The callback URL comes back here with
 * its query intact, so the caller resumes the exchange in the app itself and
 * the partner page never renders inside it.
 */
export interface NativeAuthSessionRequest {
  /** Authorization URL the sheet loads first. */
  url: string;
  /** Absolute redirect URI that ends the session. Query and fragment ignored. */
  redirectUri: string;
  /**
   * Copy for the sheet chrome. The webapp owns it because it carries eight
   * locales; the two native shells carry two.
   */
  labels: {
    /** Header title. The partner name, not a URL. */
    title: string;
    /** Title of the panel shown when the partner page fails to load. */
    errorTitle: string;
    /** Body of that panel. */
    errorMessage: string;
    /** Label of the button that reloads the partner page. */
    retry: string;
    /** Label of the button that closes the sheet. */
    close: string;
  };
}

export type NativeAuthSessionOutcome =
  /** The sheet reached the redirect URI. `url` carries the OAuth query. */
  | { status: "success"; url: string }
  /** The reader closed the sheet. Not an error: no message, no retry. */
  | { status: "cancelled" }
  /** The sheet ended on a fault. `reason` is for logs, not for the reader. */
  | { status: "failed"; reason: string }
  /** No sheet here: a browser, or a shell built before this bridge. */
  | { status: "unsupported" };

/**
 * Wire shape of one native message. `opened` is the acknowledgement: it is the
 * only proof that the shell understands `openAuthSession` at all, because a
 * WKWebView message handler drops a method it does not know without a word.
 * A shell that never acknowledges is an older build, and the caller redirects.
 */
const nativeAuthSessionMessageSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("opened") }),
  // z.url(), not z.string(): the consumer builds a `new URL` from this, and
  // `new URL("")` throws. An unusable URL is a malformed message, so it leaves
  // here as `failed` instead of as an exception nobody catches.
  z.object({ status: z.literal("success"), url: z.url() }),
  z.object({ status: z.literal("cancelled") }),
  z.object({ status: z.literal("failed"), reason: z.string() }),
]);

/**
 * How long a shell has to acknowledge before we call it too old and redirect.
 * A shell that carries the method answers in milliseconds, so this is the dead
 * time an older build costs the reader between the tap and the redirect.
 */
const ACK_TIMEOUT_MS = 3_000;
/**
 * Leak guard, not a deadline. Every exit from the sheet reports back, so this
 * fires only if the shell dies with the promise still pending.
 */
const SESSION_TIMEOUT_MS = 20 * 60 * 1_000;

/**
 * iOS evaluates `callback({...})` and passes the object; the Android
 * `@JavascriptInterface` can only carry strings, so it passes the same object
 * encoded. Both are untrusted here and go through the schema.
 */
function parseMessage(
  raw: unknown
): z.infer<typeof nativeAuthSessionMessageSchema> | null {
  if (typeof raw !== "string") {
    const parsed = nativeAuthSessionMessageSchema.safeParse(raw);
    return parsed.success ? parsed.data : null;
  }
  const decoded: unknown = (() => {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  })();
  if (decoded === null) return null;
  const parsed = nativeAuthSessionMessageSchema.safeParse(decoded);
  return parsed.success ? parsed.data : null;
}

function errorToReason(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/**
 * Registers a multi-shot native callback and resolves on the first terminal
 * message. Multi-shot because the acknowledgement arrives first and the
 * outcome only after the reader is done with the partner page.
 */
function awaitSessionOutcome(
  invoke: (callbackName: string) => void
): Promise<NativeAuthSessionOutcome> {
  return new Promise<NativeAuthSessionOutcome>((resolve) => {
    const callbackName =
      `openAuthSessionCallback_${Math.random().toString(36).slice(2, 9)}` as const;
    let ackTimeout: number | null = window.setTimeout(() => {
      ackTimeout = null;
      // Expected while shells without the sheet are still in the wild. Logged
      // so the rate is visible: it is how we know the redirect fallback can go,
      // and a supporting shell that lands here is a regression, not an old build.
      console.warn(
        `native auth session: no acknowledgement in ${ACK_TIMEOUT_MS}ms, treating the shell as too old`
      );
      finish({ status: "unsupported" });
    }, ACK_TIMEOUT_MS);
    let sessionTimeout: number | null = null;

    function finish(outcome: NativeAuthSessionOutcome): void {
      if (ackTimeout !== null) window.clearTimeout(ackTimeout);
      if (sessionTimeout !== null) window.clearTimeout(sessionTimeout);
      ackTimeout = null;
      sessionTimeout = null;
      delete window[callbackName];
      resolve(outcome);
    }

    window[callbackName] = (...args: unknown[]): void => {
      const message = parseMessage(args[0]);
      if (message === null) {
        console.error(
          `native auth session: unreadable message payload=${String(args[0])}`
        );
        finish({ status: "failed", reason: "unreadable_native_message" });
        return;
      }
      switch (message.status) {
        case "opened":
          if (ackTimeout !== null) window.clearTimeout(ackTimeout);
          ackTimeout = null;
          // A second acknowledgement replaces the handle, so clear the timer it
          // replaces or that one runs on untracked past `finish`.
          if (sessionTimeout !== null) window.clearTimeout(sessionTimeout);
          sessionTimeout = window.setTimeout(() => {
            sessionTimeout = null;
            finish({ status: "failed", reason: "native_session_timeout" });
          }, SESSION_TIMEOUT_MS);
          return;
        case "success":
          finish({ status: "success", url: message.url });
          return;
        case "cancelled":
          finish({ status: "cancelled" });
          return;
        case "failed":
          finish({ status: "failed", reason: message.reason });
          return;
        default:
          throw new Error(message satisfies never);
      }
    };

    try {
      invoke(callbackName);
    } catch (error) {
      console.error(
        `native auth session: bridge invocation failed error="${errorToReason(error)}"`
      );
      finish({ status: "failed", reason: errorToReason(error) });
    }
  });
}

export function openNativeAuthSession(
  request: NativeAuthSessionRequest
): Promise<NativeAuthSessionOutcome> {
  if (isIOSApp()) {
    const handler = getServicesHandler();
    if (handler === null) return Promise.resolve({ status: "unsupported" });
    return awaitSessionOutcome((callback) => {
      handler.postMessage({
        method: "openAuthSession",
        callback,
        url: request.url,
        redirectUri: request.redirectUri,
        labels: request.labels,
      });
    });
  }

  if (isAndroidApp()) {
    const services = window.services;
    if (services === undefined)
      return Promise.resolve({ status: "unsupported" });
    const openAuthSession = services.openAuthSession;
    // A shell built before this bridge simply has no such method on the
    // injected object. Android says so outright, where iOS needs the
    // acknowledgement above.
    if (typeof openAuthSession !== "function")
      return Promise.resolve({ status: "unsupported" });
    return awaitSessionOutcome((callback) => {
      // Called on `services` so the WebView keeps its Java binding.
      openAuthSession.call(services, JSON.stringify(request), callback);
    });
  }

  return Promise.resolve({ status: "unsupported" });
}
